import {PayType} from 'com/gongxigames/math/core/game/enums/PayType';
import {CoreDI, CurrencyFormatter, IBrowser, inject, LanguageService} from '@gongxigames/framework/core';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic/LogicEvents';
import {KeypadInfoElementsView} from '@gongxigames/framework/slot/modules/keypadInfo/KeypadInfoElementsView';
import {
    KeypadInfoElementsViewMobile
} from '@gongxigames/framework/slot/modules/keypadInfo/KeypadInfoElementsViewMobile';
import {KeypadInfoMainGameState} from '@gongxigames/framework/slot/modules/keypadInfo/states/KeypadInfoMainGameState';
import {KeypadInfoFreeSpinsState} from '@gongxigames/framework/slot/modules/keypadInfo/states/KeypadInfoFreeSpinsState';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels';
import {WinPresentationEvents} from '@gongxigames/framework/slot/modules/winpresentation';
import {BigWinEvents} from '@gongxigames/framework/slot/modules/bigwin';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {PickBonusEvents} from '@gongxigames/framework/slot/modules/pick';
import {GenericPopupEvents, GenericPopupType} from '@gongxigames/framework/slot/modules/genericPopup';
import {BuyBonusPopUpEvents} from '@gongxigames/framework/slot/modules/buyBonus';
import {KeypadControlsEvents} from '@gongxigames/framework/slot/modules/keypadControls';
import {Module} from '@gongxigames/framework/slot';

type View = KeypadInfoElementsView | KeypadInfoElementsViewMobile;

export class KeypadInfoElementsModule extends Module<View> {
    public static readonly NAME: symbol = Symbol('KeypadInfoElementsModule');

    public static readonly BASIC_STATE: string = 'basic';
    public static readonly FREESPIN_STATE: string = 'freespins';

    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;

    @inject(CoreDI.CurrencyFormatter)
    protected _currencyFormatter!: CurrencyFormatter;

    @inject(CoreDI.Browser)
    public browser!: IBrowser;

    protected _isBigWin = false;
    protected _isBonusWin = false;
    protected _isFreeGames = false;
    protected _isBigWinAfterFreeGames = false;
    protected _isBigWinAfterBonusGame = false;
    protected _isRetriggerFreeGames = false;
    protected _isFreeGamesEnd = false;

    protected setupViews(): void {
        if (this.isPlatformDesktop()) {
            this.setView(KeypadInfoElementsView.NAME);
        } else {
            this.setView(KeypadInfoElementsViewMobile.NAME);
        }
    }

    protected setupStates(): void {
        this.addState(new KeypadInfoMainGameState(this, KeypadInfoElementsModule.BASIC_STATE));
        this.addState(new KeypadInfoFreeSpinsState(this, KeypadInfoElementsModule.FREESPIN_STATE));

        this.gotoState(KeypadInfoElementsModule.BASIC_STATE);
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.on(LogicEvents.BALANCE_UPDATED, this.refreshBalance);
        this.on(LogicEvents.POINTS_BALANCE_UPDATED, this.refreshPointsBalance);

        this.onCurrentStateHandle('General.Game.INITIALIZED');
        this.onCurrentStateHandle(ReelsEvents.REELS_SPINNING_STARTED);
        this.onCurrentStateHandle(ReelsEvents.ALL_REELS_STOPPED);
        this.onCurrentStateHandle('General.WinPresentation.WIN_METER_FINISHED');
        this.onCurrentStateHandle(WinPresentationEvents.INITIAL_ANIMATION_STARTED);
        this.onCurrentStateHandle(WinPresentationEvents.PAY_ANIMATION_STARTED);
        this.onCurrentStateHandle(WinPresentationEvents.PAY_ANIMATIONS_AND_WIN_METER_FINISHED);
        this.onCurrentStateHandle(BigWinEvents.STARTED);
        this.onCurrentStateHandle(BigWinEvents.FINISHED);
        this.onCurrentStateHandle(BigWinEvents.WIN_METER_FINISHED);
        this.onCurrentStateHandle(BigWinEvents.POST_FEATURE_BW_SKIPPED);
        this.onCurrentStateHandle(FlowModuleEvents.NO_WIN);
        this.onCurrentStateHandle(KeypadControlsEvents.TOTAL_BET_CHANGED);
        this.onCurrentStateHandle(FlowModuleEvents.FREESPINS_RESTORED);
        this.onCurrentStateHandle(FlowModuleEvents.RESTORE_PICK_BONUS);
        this.onCurrentStateHandle(PickBonusEvents.STARTED);
        this.onCurrentStateHandle(PickBonusEvents.FINISHED);
        this.onCurrentStateHandle(FlowModuleEvents.RESPIN_ON_FREESPINS_RESTORED);
        this.onCurrentStateHandle('Autoplay.STOPPED');

        this.on(KeypadControlsEvents.TOTAL_BET_CHANGED, this.onTotalBetChanged);

        this.on(GenericPopupEvents.INTRO_STARTED, this.onPopupStarted);
        this.on(GenericPopupEvents.FADE_OUT_STARTED, this.onPopupFadeOutStarted);
        this.on(GenericPopupEvents.POPUP_CLOSED, this.onPopupClosed);

        this.on(BuyBonusPopUpEvents.BUY_BUTTON_CLICKED, this.onBuyButtonClicked);
    }

    protected onBuyButtonClicked(): void {
        this._isFreeGames = false;
        this.view?.refreshLeftTicker('');
        this.view?.refreshRightTicker(this.languageService.get('goodLuck'));
    }

    protected onPopupStarted(id: symbol, type: symbol): void {
        this.currentState.handle(id, type);
    }

    protected onPopupFadeOutStarted(id: symbol, type: symbol): void {
        this.currentState.handle(id, type);
    }

    protected onPopupClosed(id: symbol, type: symbol): void {
        if (type === GenericPopupType.FREESPIN_OUTRO) {
            this.refreshBalance();
        }
        this.currentState.handle(id, type);
    }

    protected onTotalBetChanged(eventID: symbol, betIndex: number, betValueText: string): void {
        this.view.refreshTotalBetInfo(betValueText);
    }

    public getFreeGamesWin(): number {
        const pays = this.logic.getSessionForFreeSpinsWinningRound().pays;
        let noFreeGamesWin = 0;

        pays.forEach((pay) => {
            if (pay.type !== PayType.SCATTER) {
                noFreeGamesWin += pay.win * pay.bet;
            }
        });

        return this.logic.getTotalWin() - noFreeGamesWin;
    }

    public refreshBalance(): void {
        this.eventManager.emit('General.UI.BALANCE.UPDATED', this.logic.getBalance());
        this.view.refreshBalance(this.logic.getBalance());
    }

    public refreshPointsBalance(_id: symbol, balance: number): void {
        this.view.refreshPointsBalance(balance);
    }

    public get languageService(): LanguageService {
        return this._languageService;
    }

    public get isBigWin(): boolean {
        return this._isBigWin;
    }

    public set isBigWin(value: boolean) {
        this._isBigWin = value;
    }

    public get isBonusWin(): boolean {
        return this._isBonusWin;
    }

    public set isBonusWin(value: boolean) {
        this._isBonusWin = value;
    }

    public get isFreeGames(): boolean {
        return this._isFreeGames;
    }

    public set isFreeGames(value: boolean) {
        this._isFreeGames = value;
    }

    public get isBigWinAfterFreeGames(): boolean {
        return this._isBigWinAfterFreeGames;
    }

    public set isBigWinAfterFreeGames(value: boolean) {
        this._isBigWinAfterFreeGames = value;
    }

    public get isBigWinAfterBonusGame(): boolean {
        return this._isBigWinAfterBonusGame;
    }

    public set isBigWinAfterBonusGame(value: boolean) {
        this._isBigWinAfterBonusGame = value;
    }

    public get isRetriggerFreeGames(): boolean {
        return this._isRetriggerFreeGames;
    }

    public set isRetriggerFreeGames(value: boolean) {
        this._isRetriggerFreeGames = value;
    }

    public get isFreeGamesEnd(): boolean {
        return this._isFreeGamesEnd;
    }

    public set isFreeGamesEnd(value: boolean) {
        this._isFreeGamesEnd = value;
    }

    public get currencyFormatter(): CurrencyFormatter {
        return this._currencyFormatter;
    }
}
